import React, { useEffect, useState, useRef } from 'react';
import { Container, Row, Col, Card, Accordion, Placeholder, Form } from 'react-bootstrap';
import { TbArrowsTransferDown, TbArrowsTransferUp } from "react-icons/tb";
import moment from 'moment';
import './CommunicationFeed.css';

interface Props {
  filteredMessages: any[];
  progress: boolean;
  activePins: any[];
}

const CommunicationFeed: React.FC<Props> = ({ filteredMessages, progress, activePins }) => {
  const [discussions, setDiscussions] = useState<any[]>([]);

  const discussionsRef = useRef<HTMLDivElement>(null);

  const [messageTypeFilter, setMessageTypeFilter] = useState<"po" | "pm" | null>(null);
  const [discussionOrder, setDiscussionOrder] = useState<"asc" | "desc">("desc");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const resetAllFilters = () => {
    setMessageTypeFilter(null)
    setDiscussionOrder("desc")
    setSearchQuery("")
  }

  

  const getDiscussionsFeed = () => {

    // search the whole discussions object
    const searchInDiscussions = (obj: any, query: string): boolean => {
      if (typeof obj === "string") {
        return obj.toLowerCase().includes(query);
      }
    
      if (Array.isArray(obj)) {
        return obj.some(item => searchInDiscussions(item, query));
      }
    
      if (typeof obj === "object" && obj !== null) {
        return Object.values(obj).some(value => searchInDiscussions(value, query));
      }
    
      return false;
    };

    // filter discussions
    const filteredDiscussions = filteredMessages.filter((message: any) => {
      
      // only active pins
      if (!activePins.includes(message.pin)) {
        return false;
      }
    
      // filter by type
      if (messageTypeFilter !== null && message.messageType !== messageTypeFilter) {
        return false;
      }
    
      // filter by content or author
      if (searchQuery.length >= 2 && !searchInDiscussions(message, searchQuery.toLowerCase())) {
        return false;
      }
    
      return true;
    });


    const sortedDiscussions = filteredDiscussions.sort((a: any, b: any) => {

      // pin ordered discussions by default
      if (a.pin !== b.pin) {
        return a.pin - b.pin;
      }

      // asc or desc time order
      if (discussionOrder === "desc") {
        return new Date(b.lastCommentedAt).getTime() - new Date(a.lastCommentedAt).getTime();
      } else {
        return new Date(a.lastCommentedAt).getTime() - new Date(b.lastCommentedAt).getTime();
      }
    })

    return sortedDiscussions;
  }

  const renderDiscussions = () => {

    const discussionsFeed = getDiscussionsFeed();
    setDiscussions(discussionsFeed);
  }

  const scrollToTop = () => {
    if (discussionsRef.current) {
      discussionsRef.current.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    }
  }

  const handleMessageTypeFilter = (type: "po" | "pm" | null) => {
    setMessageTypeFilter(type);
  }

  const handleSearchQuery = (query: string) => {
    setSearchQuery(query);
  }

  const handleDiscussionOrder = () => {
    setDiscussionOrder(discussionOrder === "desc" ? "asc" : "desc");
  }

  useEffect(() => {
    resetAllFilters();
  }, [activePins])

  useEffect(() => {
    renderDiscussions();
    scrollToTop();

  }, [activePins, filteredMessages, messageTypeFilter, discussionOrder, searchQuery]);

  // group discussions by row id
  const groupedDiscussions: Record<string, any[]> = discussions.reduce((groups: Record<string, any[]>, message: any) => {
    if (!groups[message.id]) {
      groups[message.id] = [];
    }
    groups[message.id].push(message);
    return groups;
  }, {});

  return (
    <Container
      className="communication-feed"
      style={{ height: '100%', overflow: 'hidden', color: 'black' }}
    >
      <h5 className="text-center mb-2">
        Discussions Feed
      </h5>
      {filteredMessages.length > 0 && (
        <Accordion className="mb-3">
          <Accordion.Item eventKey="0">
            <Accordion.Header>Filter Discussions</Accordion.Header>
            <Accordion.Body>
              <Row className="mb-3">
                <Col md={7}>
                  <div>
                    <Form.Label className="filter-form-label">
                      Show
                    </Form.Label>
                  </div>

                  <button className={`btn btn-sm btn-secondary me-2${messageTypeFilter === null ? " btn-all-active" : ""}`} onClick={() => handleMessageTypeFilter(null)}>All</button>
                  <button className={`btn btn-sm btn-secondary me-2${messageTypeFilter === "pm" ? " btn-pmm-active" : ""}`} onClick={() => handleMessageTypeFilter("pm")}>Only PM</button>
                  <button className={`btn btn-sm btn-secondary me-2${messageTypeFilter === "po" ? " btn-pom-active" : ""}`} onClick={() => handleMessageTypeFilter("po")}>Only PO</button>

                </Col>
                <Col md={5}>
                  <div>
                    <Form.Label className="filter-form-label">
                      Sort
                    </Form.Label>
                  </div>

                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => handleDiscussionOrder()}>
                    {discussionOrder === "desc" ? (
                      <>Sort <TbArrowsTransferDown /></>
                    ) : (
                      <>Sort <TbArrowsTransferUp /></>
                    )}
                  </button>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label htmlFor="filterAuthor" className="filter-form-label">
                      Filter by author or content
                    </Form.Label>
                    <Form.Control className="rounded-pill"
                      id="filterAuthor" type="text" onChange={(e) => handleSearchQuery(e.currentTarget.value)} value={searchQuery} />
                  </Form.Group>
                </Col>
              </Row>

            </Accordion.Body>
          </Accordion.Item>
        </Accordion>

      )}

      {progress ? (
        <div>
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index}>
              <Card className="communication-message p-2">
                <Card.Body>
                  <Placeholder as={Card.Title} animation="wave">
                    <Placeholder xs={12} bg="success" size="sm" />
                  </Placeholder>
                  <Placeholder as={Card.Title} animation="wave">
                    <Placeholder xs={8} size="xs" />
                  </Placeholder>
                  <Placeholder as={Card.Text} animation="wave">
                    <Placeholder xs={12} size="sm" bg="light" />
                    <Placeholder xs={12} size="sm" bg="light" />
                    <Placeholder xs={8} size="sm" bg="light" />
                  </Placeholder>
                  <Placeholder className="mt-4" as={Card.Title} size="sm" animation="wave">
                    <Placeholder xs={8} size="xs" />
                  </Placeholder>
                </Card.Body>
              </Card>
            </div>
          ))}
        </div>
      ) : filteredMessages.length > 0 && Object.keys(groupedDiscussions).length === 0 ? (
        <div className="text-center mt-4" style={{ paddingTop: '100px' }}>
          <div className="no-results-discussions-ph">
            No discussions matching with current search or filters criteria
          </div>
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center mt-4" style={{ paddingTop: '100px' }}>
          The discussions will be displayed here.
        </div>
      ) : (

        <div style={{ height: '100%', overflowY: 'scroll' }}
          ref={discussionsRef}>
          <Row className="g-2">
            {Object.entries(groupedDiscussions).map(([pin, messages]) => (
              <Col key={pin} md={12}>
                <Card
                  className="communication-message p-2 mb-4"
                  style={{ backgroundColor: 'white', borderRadius: 'var(--border-radius)' }}
                >
                  {/* pill */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <a
                      href={messages[0]?.permalink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', flexGrow: 1 }}
                    >
                      <div className={`discussion-pill${messages[0].po ? ' discussion-pill-po' : ''}`}>
                        <div className="discussion-pill-header">
                          <div>
                            <span className="discussion-message-type">{messages[0].po ? 'PO' : 'PM'}</span>
                            PIN# <span className="discussion-pill-pin">{messages[0]?.pin}</span>
                          </div>
                          <div className="discussion-pill-brandsub">{messages[0]?.brandSub}</div>
                        </div>
                        <div className="discussion-pill-projectname">{messages[0]?.projectName}</div>
                      </div>
                    </a>
                  </div>

                  {/* messages */}
                  {messages.map((message: any, index: number) => (
                    <div key={index}>
                      {/* comments */}
                      {message.comments.map((comment: any, commentIndex: number) => (
                        <div
                          key={commentIndex}
                          style={{
                            borderTop: commentIndex === 0 ? 'none' : '1px solid #e9ecef',
                            marginTop: commentIndex === 0 ? '0.5rem' : '1rem',
                            paddingTop: commentIndex === 0 ? 'none' : '1rem',
                            paddingLeft: commentIndex === 0 ? 'none' : '2rem',
                          }}>

                          <strong className="message-author">{comment.createdBy.email}</strong>
                          <p>{comment.text}</p>
                          <small>
                            <em>{moment(comment.createdAt).format('lll')}</em>
                          </small>
                        </div>
                      ))}
                    </div>
                  ))}
                </Card>
              </Col>
            ))}
          </Row>
        </div>


      )}
    </Container>
  );
};

export default CommunicationFeed;
